<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("kol.menu.creators") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="handleCreate()">
            <i class="fa fa-plus"></i> {{ $t("add") }}
          </base-button>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-4">
                <base-input
                  v-model="searchQuery"
                  prepend-icon="fas fa-search"
                  :placeholder="$t('search')"
                >
                </base-input>
              </div>
            </div>
          </template>
          <div>
            <el-table
              :data="queriedData"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('kol.staff')" min-width="150" prop="staff_name" sortable>
                <div slot-scope="{ row }">
                  {{ row.staff_name || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.code')" min-width="120" prop="code" sortable>
                <div slot-scope="{ row }">
                  {{ row.code || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.creator_name')" min-width="180" prop="name" sortable>
                <div slot-scope="{ row }">
                  {{ row.name }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.channel_link')" min-width="200">
                <div slot-scope="{ row }">
                  <a v-if="row.channel_link" :href="safeHref(row.channel_link)" target="_blank" rel="noopener noreferrer">
                    {{ row.channel_link }}
                  </a>
                  <span v-else>-</span>
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.follower_count')" min-width="130" align="right">
                <div slot-scope="{ row }">
                  {{ row.follower_count ? row.follower_count.toLocaleString() : '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.line_contact')" min-width="150">
                <div slot-scope="{ row }">
                  {{ row.line_contact || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('status')" min-width="100" align="center">
                <div slot-scope="{ row }">
                  <badge :type="row.is_active ? 'success' : 'secondary'" class="badge-lg">
                    {{ row.is_active ? $t('enabled') : $t('disabled') }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.menu.product_sends')" min-width="130" align="center">
                <div slot-scope="{ row }">
                  <base-button size="sm" type="info" @click.native="handleViewSendLogs(row)">
                    <i class="fa fa-list"></i> {{ $t('kol.menu.product_sends') }}
                  </base-button>
                </div>
              </el-table-column>

              <el-table-column :label="$t('actions')" min-width="120" align="center">
                <div slot-scope="{ row }" class="d-flex justify-content-center">
                  <base-button
                    @click.native="handleEdit(row)"
                    class="edit"
                    type="warning"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-edit"></i>
                  </base-button>
                  <base-button
                    @click.native="handleDelete(row)"
                    class="remove btn-link"
                    type="danger"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-trash"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            v-if="queriedData.length != 0"
          >
            <div class="">
              <p class="card-category">
                {{ $t("data") }} {{ from + 1 }} {{ $t("to") }} {{ to }}
                {{ $t("of") }} {{ total }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- Send Logs Modal -->
    <el-dialog
      :title="$t('kol.menu.product_sends') + (selectedCreatorName ? ' — ' + selectedCreatorName : '')"
      :visible.sync="showSendLogsDialog"
      width="700px"
    >
      <el-table :data="creatorSends" size="small" v-loading="sendsLoading" max-height="400">
        <el-table-column :label="$t('date')" min-width="110">
          <template v-slot="{ row }">{{ formatDate(row.order_date || row.created_at) }}</template>
        </el-table-column>
        <el-table-column prop="tracking_number" :label="$t('tracking_number')" min-width="140"></el-table-column>
        <el-table-column prop="recipient_name" :label="$t('name')" min-width="130"></el-table-column>
        <el-table-column prop="order_status" :label="$t('order_status')" min-width="100" align="center">
          <template v-slot="{ row }">{{ row.order_status || '-' }}</template>
        </el-table-column>
        <el-table-column prop="order_amount" :label="$t('amount')" min-width="100" align="right">
          <template v-slot="{ row }">{{ row.order_amount ? row.order_amount.toLocaleString() : '-' }}</template>
        </el-table-column>
        <el-table-column :label="$t('action')" min-width="90" align="center">
          <template v-slot="{ row }">
            <base-button v-if="row.order_id" size="sm" type="primary" @click="goToOrder(row.order_id)">
              {{ $t('detail') }}
            </base-button>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <base-button type="secondary" @click="showSendLogsDialog = false">{{ $t('close') }}</base-button>
      </span>
    </el-dialog>

    <!-- Create/Edit Modal -->
    <modal :show.sync="showModal" modal-classes="modal-secondary modal-lg">
      <h5 slot="header" class="modal-title">
        {{ isEdit ? $t('kol.edit_creator') : $t('kol.create_creator') }}
      </h5>
      <form class="new-event--form" @submit.prevent="">
        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('kol.creator_name')" v-model="formData.name" required>
            </base-input>
          </div>
          <div class="col-md-6">
            <base-input :label="$t('kol.code')" v-model="formData.code">
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <base-input :label="$t('kol.channel_link')" v-model="formData.channel_link">
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.follower_count') }}</label>
              <el-input-number
                v-model="formData.follower_count"
                :min="0"
                controls-position="right"
                class="w-100"
              >
              </el-input-number>
            </div>
          </div>
          <div class="col-md-6">
            <base-input :label="$t('kol.line_contact')" v-model="formData.line_contact">
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <base-input label="Email" type="email" v-model="formData.email" placeholder="email@example.com">
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('kol.tax_id')" v-model="formData.tax_id">
            </base-input>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.bank_name') }}</label>
              <el-select v-model="formData.bank_name" filterable :placeholder="$t('select')" class="w-100">
                <el-option v-for="bank in thaibanks" :key="bank" :label="bank" :value="bank"></el-option>
              </el-select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <base-input :label="$t('kol.bank_account')" v-model="formData.bank_account">
            </base-input>
          </div>
        </div>

        <div class="row" v-if="isEdit">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-control-label">ID Card</label>
              <div class="d-flex align-items-center">
                <input type="file" ref="fileIdCard" accept="image/*,application/pdf" @change="onPickFile('id_card', $event)" />
                <span v-if="formData.has_id_card" class="ml-2 text-success"><i class="fa fa-check"></i> uploaded</span>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-control-label">Bookbank</label>
              <div class="d-flex align-items-center">
                <input type="file" ref="fileBookbank" accept="image/*,application/pdf" @change="onPickFile('bookbank', $event)" />
                <span v-if="formData.has_bookbank" class="ml-2 text-success"><i class="fa fa-check"></i> uploaded</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <base-input :label="$t('kol.address')" v-model="formData.address">
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-control-label">{{ $t('recipent_district') }}</label>
              <base-input
                v-model="formData.district"
                placeholder="ตำบล / อำเภอ / จังหวัด / รหัสไปรษณีย์"
                @input="onChange"
                @keydown.down.native="onArrowDown"
                @keydown.up.native="onArrowUp"
                @keydown.enter.native="onEnter"
              ></base-input>
              <ul v-show="isOpen" class="autocomplete-results">
                <li
                  v-for="(result, i) in filteredAddress"
                  :key="i"
                  @click="setResult(result)"
                  class="autocomplete-result"
                  :class="{ 'is-active': i === arrowCounter }"
                >
                  <template v-if="result.ProvinceID == 10">
                    แขวง{{ result.District }} เขต{{ result.City }} จังหวัด{{ result.Province }} {{ result.Zipcode }}
                  </template>
                  <template v-else>
                    ต.{{ result.District }} อ.{{ result.City }} จ.{{ result.Province }} {{ result.Zipcode }}
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <base-input :label="$t('recipent_city')" v-model="formData.city" readonly></base-input>
          </div>
          <div class="col-md-4">
            <base-input :label="$t('recipent_province')" v-model="formData.province" readonly></base-input>
          </div>
          <div class="col-md-4">
            <base-input :label="$t('recipent_zipcode')" v-model="formData.zipcode" readonly></base-input>
          </div>
        </div>

        <div class="form-group">
          <label class="form-control-label">{{ $t('status') }}</label>
          <div>
            <el-switch
              v-model="formData.is_active"
              active-color="#13ce66"
              inactive-color="#909399"
            >
            </el-switch>
            <span class="ml-2">{{ formData.is_active ? $t('enabled') : $t('disabled') }}</span>
          </div>
        </div>
      </form>

      <template slot="footer">
        <button type="submit" class="btn btn-md btn-primary" @click="handleSave()">
          {{ $t("save") }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Switch, InputNumber, Input, Dialog } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, Badge } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";
import KOLProvider from "@/resource/kol";

const kol = new KOLProvider();

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [InputNumber.name]: InputNumber,
    [Input.name]: Input,
    [Dialog.name]: Dialog,
  },
  computed: {
    ...mapGetters({
      tableData: "KOL/getCreators",
      FullAddr: "Order/getAddress",
    }),
    filteredAddress() {
      let result = this.FullAddr.filter(item => {
        let key = this.formData.district.split(" ");
        if (key.length == 1) {
          return item.District.includes(key[0]) || item.Zipcode == key[0];
        }
        if (key.length == 2) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]);
        }
        if (key.length >= 3) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]) && item.Province.includes(key[2]);
        }
        return false;
      });
      return result.slice(0, 15);
    },
  },
  data() {
    return {
      propsToSearch: ["name", "code", "channel_link"],
      loading: false,
      isOpen: false,
      arrowCounter: -1,
      thaibanks: [
        "ธนาคารกรุงเทพ (BBL)",
        "ธนาคารกสิกรไทย (KBANK)",
        "ธนาคารกรุงไทย (KTB)",
        "ธนาคารทหารไทยธนชาต (TTB)",
        "ธนาคารไทยพาณิชย์ (SCB)",
        "ธนาคารกรุงศรีอยุธยา (BAY)",
        "ธนาคารเกียรตินาคินภัทร (KKP)",
        "ธนาคารซีไอเอ็มบีไทย (CIMBT)",
        "ธนาคารทิสโก้ (TISCO)",
        "ธนาคารยูโอบี (UOB)",
        "ธนาคารไทยเครดิต (TCD)",
        "ธนาคารแลนด์ แอนด์ เฮ้าส์ (LH Bank)",
        "ธนาคารอิสลามแห่งประเทศไทย (IBANK)",
        "ธนาคารออมสิน (GSB)",
        "ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (BAAC)",
        "ธนาคารอาคารสงเคราะห์ (GHB)",
        "ธนาคารพัฒนาวิสาหกิจขนาดกลางและขนาดย่อมแห่งประเทศไทย (SME D Bank)",
        "ธนาคารเพื่อการส่งออกและนำเข้าแห่งประเทศไทย (EXIM Bank)",
      ],
      creatorSends: [],
      sendsLoading: false,
      showSendLogsDialog: false,
      selectedCreatorName: "",
      showModal: false,
      isEdit: false,
      formData: {
        id: 0,
        staff_id: 0,
        name: "",
        code: "",
        channel_link: "",
        follower_count: 0,
        line_contact: "",
        email: "",
        tax_id: "",
        bank_account: "",
        bank_name: "",
        has_id_card: false,
        has_bookbank: false,
        address: "",
        district_id: 0,
        district: "",
        city_id: 0,
        city: "",
        province_id: 0,
        province: "",
        zipcode: "",
        is_active: true,
      },
    };
  },
  mounted() {
    this.loadData();
    if (this.FullAddr.length == 0) {
      this.listFullAddr();
    }
  },
  methods: {
    ...mapActions({
      listCreators: "KOL/listCreators",
      createCreator: "KOL/createCreator",
      updateCreator: "KOL/updateCreator",
      deleteCreator: "KOL/deleteCreator",
      listFullAddr: "Order/listAddressV2",
    }),
    async handleViewSendLogs(row) {
      this.selectedCreatorName = row.name;
      this.creatorSends = [];
      this.showSendLogsDialog = true;
      this.sendsLoading = true;
      try {
        const res = await kol.ProductSendList({ creator_id: row.id, per_page: 200 });
        this.creatorSends = res.data.Data[0] ? res.data.Data[0].items || [] : [];
      } catch (err) {
        console.log(err);
      }
      this.sendsLoading = false;
    },
    goToOrder(orderId) {
      this.$router.push(`/backoffice/orders/${orderId}`);
    },
    formatDate(d) {
      return d ? String(d).substring(0, 10) : '-';
    },
    safeHref(url) {
      if (!url) return '#';
      if (/^https?:\/\//i.test(url)) return url;
      return '#';
    },
    maskValue(val) {
      if (!val || val.length <= 4) return val || '';
      return '*'.repeat(val.length - 4) + val.slice(-4);
    },
    async onPickFile(type, ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file || !this.formData.id) return;
      try {
        await kol.CreatorUploadFile(this.formData.id, type, file);
        if (type === "id_card") this.formData.has_id_card = true;
        else this.formData.has_bookbank = true;
        swal.fire({
          title: "Uploaded",
          icon: "success",
          confirmButtonClass: "btn btn-success btn-fill",
          buttonsStyling: false,
        });
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || "Upload failed";
        swal.fire({
          title: msg,
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
      }
      ev.target.value = "";
    },
    async loadData() {
      this.loading = true;
      try {
        await this.listCreators({});
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.isEdit = false;
      this.isOpen = false;
      this.arrowCounter = -1;
      this.formData = {
        id: 0,
        staff_id: 0,
        name: "",
        code: "",
        channel_link: "",
        follower_count: 0,
        line_contact: "",
        email: "",
        tax_id: "",
        bank_account: "",
        bank_name: "",
        has_id_card: false,
        has_bookbank: false,
        address: "",
        district_id: 0,
        district: "",
        city_id: 0,
        city: "",
        province_id: 0,
        province: "",
        zipcode: "",
        is_active: true,
      };
      this.showModal = true;
    },
    handleEdit(row) {
      this.isEdit = true;
      this.isOpen = false;
      this.arrowCounter = -1;
      this.formData = {
        id: row.id,
        staff_id: row.staff_id || 0,
        name: row.name,
        code: row.code || "",
        channel_link: row.channel_link || "",
        follower_count: row.follower_count || 0,
        line_contact: row.line_contact || "",
        email: row.email || "",
        tax_id: row.tax_id || "",
        bank_account: row.bank_account || "",
        bank_name: row.bank_name || "",
        has_id_card: !!row.has_id_card,
        has_bookbank: !!row.has_bookbank,
        address: row.address || "",
        district_id: row.district_id || 0,
        district: row.district || "",
        city_id: row.city_id || 0,
        city: row.city || "",
        province_id: row.province_id || 0,
        province: row.province || "",
        zipcode: row.zipcode || "",
        is_active: row.is_active,
      };
      this.showModal = true;
    },
    handleSave() {
      var self = this;
      if (!this.formData.name) {
        swal.fire({
          title: this.$t("errors.invalid_param"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        return;
      }

      const payload = {
        staff_id: this.formData.staff_id,
        name: this.formData.name,
        code: this.formData.code,
        channel_link: this.formData.channel_link,
        follower_count: this.formData.follower_count,
        line_contact: this.formData.line_contact,
        email: this.formData.email,
        tax_id: this.formData.tax_id,
        bank_account: this.formData.bank_account,
        bank_name: this.formData.bank_name,
        address: this.formData.address,
        district_id: this.formData.district_id,
        district: this.formData.district,
        city_id: this.formData.city_id,
        city: this.formData.city,
        province_id: this.formData.province_id,
        province: this.formData.province,
        zipcode: this.formData.zipcode,
        is_active: this.formData.is_active,
      };

      if (this.formData.tax_id && this.formData.tax_id.includes('*')) {
        delete payload.tax_id;
      }
      if (this.formData.bank_account && this.formData.bank_account.includes('*')) {
        delete payload.bank_account;
      }

      if (this.isEdit) {
        this.updateCreator({ id: this.formData.id, payload })
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("edit_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        this.createCreator(payload)
          .then(function () {
            self.showModal = false;
            self.loadData();
            swal.fire({
              title: self.$t("create_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },
    handleDelete(row) {
      var self = this;
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question") + row.name,
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false,
        })
        .then(function (result) {
          if (result.value) {
            self.deleteCreator(row.id)
              .then(function () {
                self.loadData();
                swal.fire({
                  title: self.$t("delete_success"),
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false,
                });
              })
              .catch(function (err) {
                var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
                swal.fire({
                  title: self.$t("error"),
                  text: errorMsg,
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false,
                });
              });
          }
        });
    },
    onChange() {
      this.isOpen = true;
    },
    onArrowDown() {
      if (this.arrowCounter < this.filteredAddress.length - 1) {
        this.arrowCounter = this.arrowCounter + 1;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter - 1;
      }
    },
    onEnter() {
      if (this.arrowCounter >= 0 && this.filteredAddress[this.arrowCounter]) {
        this.setResult(this.filteredAddress[this.arrowCounter]);
      }
      this.arrowCounter = -1;
      this.isOpen = false;
    },
    setResult(r) {
      this.formData.district_id = r.DistrictID;
      this.formData.district = r.District;
      this.formData.city_id = r.CityID;
      this.formData.city = r.City;
      this.formData.province_id = r.ProvinceID;
      this.formData.province = r.Province;
      this.formData.zipcode = r.Zipcode;
      this.isOpen = false;
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
.badge-lg {
  font-size: 0.8rem;
  padding: 0.5em 0.8em;
}
.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  height: 120px;
  min-height: 1em;
  max-height: 6em;
  overflow: auto;
}
.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 2px;
  cursor: pointer;
}
.autocomplete-result:hover,
.autocomplete-result.is-active {
  background-color: #4aae9b;
  color: white;
}
</style>
